/**
 * Admin Claims API Client
 *
 * Provides methods for managing insurance claims in admin/backoffice context.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

import type { AdminClaim } from "@neutrino/ui-equinox-types";

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface AdminClaimsResponse {
  claims: AdminClaim[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface AdminClaimProcessingEvent {
  event_type: string;
  timestamp: string;
  actor: string;
  notes: string;
}

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type AdminClaimsFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: AdminClaimsFetchFunction = defaultFetch;

export function configureAdminClaimsApi(
  fetchFn: AdminClaimsFetchFunction,
): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const adminClaimsApi = {
  async fetchAll(
    params?: {
      limit?: number;
      offset?: number;
      status?: string;
      priority?: string;
      claimType?: string;
      search?: string;
    },
    baseUrl = "/api/v1",
  ): Promise<AdminClaimsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.status) query.set("status", params.status);
    if (params?.priority) query.set("priority", params.priority);
    if (params?.claimType) query.set("claim_type", params.claimType);
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    const res = await _fetch(`${baseUrl}/admin/claims${qs ? `?${qs}` : ""}`);
    return unwrap<AdminClaimsResponse>(res);
  },

  async fetchById(claimId: string, baseUrl = "/api/v1"): Promise<AdminClaim> {
    const res = await _fetch(`${baseUrl}/admin/claims/${claimId}`);
    return unwrap<AdminClaim>(res);
  },

  async updateClaimStatus(
    claimId: string,
    status: string,
    notes?: string,
    baseUrl = "/api/v1",
  ): Promise<AdminClaim> {
    const res = await _fetch(`${baseUrl}/admin/claims/${claimId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, notes }),
    });
    return unwrap<AdminClaim>(res);
  },

  async approveClaim(
    claimId: string,
    notes?: string,
    baseUrl = "/api/v1",
  ): Promise<AdminClaim> {
    const res = await _fetch(`${baseUrl}/admin/claims/${claimId}/approve`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    });
    return unwrap<AdminClaim>(res);
  },

  async denyClaim(
    claimId: string,
    reason: string,
    baseUrl = "/api/v1",
  ): Promise<AdminClaim> {
    const res = await _fetch(`${baseUrl}/admin/claims/${claimId}/deny`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
    return unwrap<AdminClaim>(res);
  },

  async assignClaim(
    claimId: string,
    adjuster: string,
    baseUrl = "/api/v1",
  ): Promise<AdminClaim> {
    const res = await _fetch(`${baseUrl}/admin/claims/${claimId}/assign`, {
      method: "PATCH",
      body: JSON.stringify({ assigned_adjuster: adjuster }),
    });
    return unwrap<AdminClaim>(res);
  },

  async fetchProcessingHistory(
    claimId: string,
    baseUrl = "/api/v1",
  ): Promise<AdminClaimProcessingEvent[]> {
    const query = new URLSearchParams({
      entity_type: "claim",
      entity_id: claimId,
    });
    const res = await _fetch(`${baseUrl}/admin/audit?${query.toString()}`);
    return unwrap<AdminClaimProcessingEvent[]>(res);
  },
};
